import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Create a context for feature flags
const FeatureFlagContext = createContext({
  flags: {},
  isLoading: true,
  getFlag: () => false,
  getAllFlags: () => ({}),
});

/**
 * Provider component that fetches and caches feature flags
 * This makes a single call to LaunchDarkly and provides flags to all child components
 */
export const FeatureFlagProvider = ({ children }) => {
  // Fetch all flags from LaunchDarkly (this is the only direct call to LaunchDarkly)
  const ldFlags = useFlags();
  const [isLoading, setIsLoading] = useState(true);

  // Store flags in local state
  const [flags, setFlags] = useState({});

  // Update flags when LaunchDarkly returns them
  useEffect(() => {
    if (ldFlags && Object.keys(ldFlags).length > 0) {
      setFlags(ldFlags);
      setIsLoading(false);

      // Optionally cache flags in localStorage for faster initial loads
      try {
        localStorage.setItem(
          'featureFlags',
          JSON.stringify({
            flags: ldFlags,
            timestamp: Date.now(),
          })
        );
      } catch (error) {
        console.warn('Failed to cache feature flags in localStorage:', error);
      }
    }
  }, [ldFlags]);

  // Try to load cached flags from localStorage on initial render
  useEffect(() => {
    try {
      const cachedData = localStorage.getItem('featureFlags');
      if (cachedData) {
        const { flags: cachedFlags, timestamp } = JSON.parse(cachedData);

        // Only use cached flags if they're less than 5 minutes old
        const fiveMinutes = 5 * 60 * 1000;
        if (Date.now() - timestamp < fiveMinutes) {
          setFlags(cachedFlags);
        }
      }
    } catch (error) {
      console.warn('Failed to load cached feature flags:', error);
    }
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      // All flags
      flags,

      // Loading state
      isLoading,

      // Function to get a specific flag with a default value
      getFlag: (flagKey, defaultValue = false) => {
        return flags[flagKey] !== undefined ? flags[flagKey] : defaultValue;
      },

      // Function to get all flags
      getAllFlags: () => flags,
    }),
    [flags, isLoading]
  );

  return (
    <FeatureFlagContext.Provider value={contextValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

/**
 * Hook to access a specific feature flag
 * @param {string} flagKey - The key of the feature flag to access
 * @param {any} defaultValue - The default value to return if the flag is not found
 * @returns {any} The value of the feature flag
 */
export const useFeatureFlagFromContext = (flagKey, defaultValue = false) => {
  const context = useContext(FeatureFlagContext);

  // If context is not available (component not wrapped in provider), return null
  // This allows the backward compatibility hooks to detect and fall back
  if (!context || !context.getFlag) {
    return null;
  }

  return context.getFlag(flagKey, defaultValue);
};

/**
 * Hook to access all feature flags
 * @returns {Object} All feature flags
 */
export const useFeatureFlagsFromContext = () => {
  const context = useContext(FeatureFlagContext);

  // If context is not available (component not wrapped in provider), return null
  // This allows the backward compatibility hooks to detect and fall back
  if (!context || !context.getAllFlags) {
    console.warn(
      'FeatureFlagContext is not available. Ensure your component is wrapped in a FeatureFlagProvider.'
    );
    return null;
  }

  return context.getAllFlags();
};

/**
 * Hook to check if feature flags are still loading
 * @returns {boolean} True if flags are still loading
 */
export const useFeatureFlagsLoading = () => {
  const { isLoading } = useContext(FeatureFlagContext);
  return isLoading;
};

export default FeatureFlagContext;
